/**
 * Navigation node representing a walkable surface in the world.
 * Each node corresponds to the top surface of a cuboid.
 */
export default class NavNode {
  /**
   * Create a navigation node
   * @param {string} cuboidKey - Key of the source cuboid
   * @param {number} x - Center X coordinate of walkable surface
   * @param {number} y - Height (Y) of the walkable surface
   * @param {number} z - Center Z coordinate of walkable surface
   * @param {number} width - Width of walkable area
   * @param {number} depth - Depth of walkable area
   */
  constructor(cuboidKey, x, y, z, width, depth) {
    this.cuboidKey = cuboidKey;
    this.x = x;
    this.y = y;
    this.z = z;
    this.width = width;
    this.depth = depth;

    // Adjacent nodes with traversal costs: [{node: NavNode, cost: number}]
    this.neighbors = [];

    // Surface grid for fine-grained pathfinding (set by NavMeshBuilder)
    this.grid = null;

    // GridPathfinder instance for this surface (set by NavMeshBuilder)
    this.gridPathfinder = null;

    // Bounds for quick overlap checks
    this.minX = x - width / 2;
    this.maxX = x + width / 2;
    this.minZ = z - depth / 2;
    this.maxZ = z + depth / 2;
  }

  /**
   * Add a neighbor connection
   * @param {NavNode} node - The neighboring node
   * @param {number} cost - Traversal cost to this neighbor
   */
  addNeighbor(node, cost) {
    // Avoid duplicates
    if (!this.neighbors.some(n => n.node === node)) {
      this.neighbors.push({ node, cost });
    }
  }

  /**
   * Remove a neighbor connection
   * @param {NavNode} node - The neighbor to remove
   */
  removeNeighbor(node) {
    this.neighbors = this.neighbors.filter(n => n.node !== node);
  }

  /**
   * Check if this node's surface contains a point (on XZ plane)
   * @param {number} px - Point X
   * @param {number} pz - Point Z
   * @returns {boolean}
   */
  containsPoint(px, pz) {
    return px >= this.minX && px <= this.maxX &&
           pz >= this.minZ && pz <= this.maxZ;
  }

  /**
   * Get squared distance from this node's center to a point
   * @param {number} px - Point X
   * @param {number} py - Point Y
   * @param {number} pz - Point Z
   * @returns {number} Squared distance
   */
  distanceSquaredTo(px, py, pz) {
    const dx = this.x - px;
    const dy = this.y - py;
    const dz = this.z - pz;
    return dx * dx + dy * dy + dz * dz;
  }

  /**
   * Get distance from this node's center to a point
   * @param {number} px - Point X
   * @param {number} py - Point Y
   * @param {number} pz - Point Z
   * @returns {number} Distance
   */
  distanceTo(px, py, pz) {
    return Math.sqrt(this.distanceSquaredTo(px, py, pz));
  }

  /**
   * Check if this node's surface overlaps with another on the XZ plane
   * @param {NavNode} other - Other node to check
   * @returns {boolean}
   */
  overlapsXZ(other) {
    return this.minX < other.maxX && this.maxX > other.minX &&
           this.minZ < other.maxZ && this.maxZ > other.minZ;
  }

  /**
   * Check if this node's surface touches another (edges meet)
   * @param {NavNode} other - Other node to check
   * @param {number} tolerance - Tolerance for edge matching (default 0.01)
   * @returns {boolean}
   */
  touchesXZ(other, tolerance = 0.01) {
    // Check if edges are adjacent (within tolerance) on X or Z axis
    const touchesX = Math.abs(this.maxX - other.minX) <= tolerance ||
                     Math.abs(this.minX - other.maxX) <= tolerance;
    const touchesZ = Math.abs(this.maxZ - other.minZ) <= tolerance ||
                     Math.abs(this.minZ - other.maxZ) <= tolerance;

    // Must touch on one axis and overlap on the other
    if (touchesX) {
      // Check Z overlap
      return this.minZ < other.maxZ && this.maxZ > other.minZ;
    }
    if (touchesZ) {
      // Check X overlap
      return this.minX < other.maxX && this.maxX > other.minX;
    }

    return false;
  }

  /**
   * Get a unique identifier for this node
   * @returns {string}
   */
  getId() {
    return this.cuboidKey;
  }

  /**
   * Set the surface grid for this node
   * @param {SurfaceGrid} grid - The surface grid
   * @param {GridPathfinder} pathfinder - The grid pathfinder
   */
  setGrid(grid, pathfinder) {
    this.grid = grid;
    this.gridPathfinder = pathfinder;
  }

  /**
   * Check if this node has a grid for fine-grained pathfinding
   * @returns {boolean}
   */
  hasGrid() {
    return this.grid !== null && this.gridPathfinder !== null;
  }
}
